/**
 * B2B Conversion & Event Tracking Layer (GA4 + GTM)
 *
 * Implements client-directed B2B KPI tracking:
 * - RFQ & Inquiry Form Submissions
 * - Sample Material Requests
 * - WhatsApp Conversations & Direct Chats
 * - Mailto / Email Clicks
 * - Phone Call Clicks
 * - Catalogue Views & PDF Downloads
 * - VIP Booth / Showroom Meetings Booked
 */

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

export type ConversionEvent =
  | 'rfq_inquiry'
  | 'sample_request'
  | 'whatsapp_click'
  | 'email_click'
  | 'phone_click'
  | 'catalogue_download'
  | 'meeting_booked';

interface TrackingParams {
  event_category?: string;
  event_label?: string;
  inquiry_type?: 'rfq' | 'sample' | 'general' | 'exhibition' | 'custom_oem';
  product_code?: string;
  source_page?: string;
  language?: string;
  click_location?: string;
  [key: string]: any;
}

/**
 * Pushes a structured event to window.dataLayer and window.gtag
 */
export function trackEvent(eventName: ConversionEvent | string, params: TrackingParams = {}) {
  try {
    const currentLang = document.documentElement.lang || 'en';
    const currentPath = window.location.pathname;

    const payload = {
      event: eventName,
      language: params.language || currentLang,
      source_page: params.source_page || currentPath,
      timestamp: new Date().toISOString(),
      ...params,
    };

    // 1. Google Tag Manager dataLayer
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);

      // 2. Direct GA4 gtag if installed
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }

      // 3. Dev logger
      if (import.meta.env.DEV) {
        console.log(`🎯 [B2B Analytics] Event tracked: ${eventName}`, payload);
      }
    }
  } catch (err) {
    console.warn('[Analytics Error]', err);
  }
}

// Shortcut helpers for high-value B2B conversion goals:

/** Track WhatsApp chat initiation */
export function trackWhatsAppClick(location: string, productCode?: string) {
  trackEvent('whatsapp_click', {
    event_category: 'Direct Lead',
    event_label: `WhatsApp from ${location}${productCode ? ` (Item: ${productCode})` : ''}`,
    click_location: location,
    product_code: productCode,
  });
}

/** Track Email lead initiation */
export function trackEmailClick(location: string, email: string = 'ann@clouds-art.com') {
  trackEvent('email_click', {
    event_category: 'Direct Lead',
    event_label: `Email to ${email} from ${location}`,
    click_location: location,
  });
}

/** Track Phone call initiation */
export function trackPhoneClick(location: string, phone: string = '+86 18943549167') {
  trackEvent('phone_click', {
    event_category: 'Direct Lead',
    event_label: `Call to ${phone} from ${location}`,
    click_location: location,
  });
}

/** Track RFQ / Inquiry form submission */
export function trackRfqSubmission(data: {
  inquiry_type?: 'rfq' | 'sample' | 'general' | 'exhibition' | 'custom_oem';
  subject?: string;
  hasProductCode?: boolean;
}) {
  trackEvent('rfq_inquiry', {
    event_category: 'Form Submission',
    event_label: data.subject || 'B2B Inquiry',
    inquiry_type: data.inquiry_type || 'rfq',
    // GA4 standard lead event alias
    value: 1,
    currency: 'USD',
  });
}

/** Track Sample Material Request */
export function trackSampleRequest(materialOrCollection: string) {
  trackEvent('sample_request', {
    event_category: 'High-Intent Action',
    event_label: `Sample request: ${materialOrCollection}`,
    inquiry_type: 'sample',
  });
}

/** Track Catalogue PDF / Download request */
export function trackCatalogueDownload(categoryName?: string) {
  trackEvent('catalogue_download', {
    event_category: 'Engagement',
    event_label: categoryName ? `Catalogue: ${categoryName}` : 'Full Catalogue',
  });
}

/** Track Trade Fair VIP or Studio Meeting reservation */
export function trackMeetingBooked(meetingType: 'booth' | 'studio', details?: string) {
  trackEvent('meeting_booked', {
    event_category: 'High-Intent Action',
    event_label: `Meeting: ${meetingType} - ${details || ''}`,
    inquiry_type: 'exhibition',
  });
}
